// 扫码上传 — a 「扫码上传」 button next to every image upload control in 客户记录. It opens a dialog
// with a one-time QR code (15 min, one slot only); the phone opens <origin><basename>/u/<token>
// (pages/public/PhoneUpload.js, no login) and uploads; this dialog polls every 2 s and hands the
// new photos back:
//   • kind 'step' (双方截图): the server already wrote service_step_attachments → onChanged()
//   • every other kind: onFiles(urls) — the caller appends them to the form it has open, exactly
//     like a desktop upload (the record is still saved by the coach).
// The token is revoked when the dialog closes (完成 / ✕ / unmount) and on 「换一个二维码」.
// Before the backend is deployed GET /upload-tokens/config 404s → the buttons render nothing.
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { QrCode, X } from 'lucide-react';
import QRCode from 'qrcode';
import { toast } from 'react-toastify';
import { uploadTokensAPI } from '../../../services/api';
import { resolvePhotoUrl } from '../../../utils/photoUtils';

const INK = '#111111';
const SUB = '#3A3835';
const MUTE = '#6E685F';
const GOLD_TEXT = '#8A6D3B';
const LINE = 'rgba(17,17,17,.12)';
const SERIF = "'Cormorant Garamond', 'Noto Serif SC', serif";

// One capability probe per page load (cheap GET; 404 before deploy → hidden).
let capPromise = null;
let capValue = null;
function probe() {
  if (!capPromise) {
    capPromise = uploadTokensAPI.config()
      .then((r) => { capValue = !!(r.data && r.data.enabled); return capValue; })
      .catch(() => { capValue = false; return false; });
  }
  return capPromise;
}
export function useQrUploadEnabled() {
  const [on, setOn] = useState(capValue === true);
  useEffect(() => {
    let off = false;
    if (capValue === null) probe().then((v) => { if (!off) setOn(v); });
    return () => { off = true; };
  }, []);
  return on;
}

// The phone link: whatever site the coach is on (US / HK / /preview) + the app's basename.
export function phoneUploadUrl(token) {
  let base = process.env.PUBLIC_URL || '';
  if (/^https?:\/\//i.test(base)) { try { base = new URL(base).pathname; } catch (e) { base = ''; } }
  base = base.replace(/\/$/, '');
  return `${window.location.origin}${base}/u/${token}`;
}

const mmss = (sec) => {
  const s = Math.max(0, Math.floor(sec));
  return `${String(Math.floor(s / 60)).padStart(2, '0')}:${String(s % 60).padStart(2, '0')}`;
};

/**
 * Props: profileId, target ({ kind, ... }), where (desktop-only line: member · step · party),
 * onFiles(urls) for local kinds, onChanged() for kind 'step', isZh,
 * variant 'tile' (square, like the add tile; tileW / tileH) | 'inline' (small ghost button) | 'icon',
 * disabled, style (merged into the button).
 */
export function QrUploadButton({ profileId, target, where = '', onFiles, onChanged, isZh = true, variant = 'inline', tileW = 60, tileH = 80, disabled = false, style }) {
  const enabled = useQrUploadEnabled();
  const [open, setOpen] = useState(false);
  const t = (zh, en) => (isZh ? zh : en);
  if (!enabled || !profileId) return null;
  const label = t('扫码上传', 'Scan to upload');
  let btn;
  if (variant === 'tile') {
    btn = (
      <button type="button" disabled={disabled} onClick={() => setOpen(true)} aria-label={label} title={t('用手机扫码上传照片', 'Upload from your phone')}
        style={{ width: tileW, height: tileH, flex: 'none', borderRadius: 2, border: `1px solid ${INK}`, background: '#FFFDF9', color: INK, fontFamily: 'inherit', fontSize: 11.5, lineHeight: 1.2, cursor: disabled ? 'not-allowed' : 'pointer', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 4, padding: 2, opacity: disabled ? 0.5 : 1, ...style }}>
        <QrCode size={16} strokeWidth={1.4} />{label}
      </button>
    );
  } else if (variant === 'icon') {
    btn = (
      <button type="button" disabled={disabled} onClick={() => setOpen(true)} aria-label={label} title={label}
        style={{ width: 32, height: 30, border: `1px solid ${LINE}`, borderRadius: 2, background: '#FFFDF9', color: SUB, cursor: 'pointer', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', ...style }}>
        <QrCode size={16} strokeWidth={1.4} />
      </button>
    );
  } else {
    btn = (
      <button type="button" disabled={disabled} onClick={() => setOpen(true)}
        style={{ display: 'inline-flex', alignItems: 'center', gap: 6, height: 30, padding: '0 12px', borderRadius: 2, border: `1px solid ${INK}`, background: 'transparent', color: INK, fontSize: 12.5, letterSpacing: '.08em', cursor: 'pointer', fontFamily: 'inherit', whiteSpace: 'nowrap', ...style }}>
        <QrCode size={13} strokeWidth={1.4} />{label}
      </button>
    );
  }
  return (
    <>
      {btn}
      {open && <QrUploadDialog profileId={profileId} target={target} where={where} onFiles={onFiles} onChanged={onChanged} isZh={isZh} onClose={() => setOpen(false)} />}
    </>
  );
}

export function QrUploadDialog({ profileId, target, where, onFiles, onChanged, isZh = true, onClose }) {
  const t = (zh, en) => (isZh ? zh : en);
  const [tok, setTok] = useState(null);          // { id, token, expires_at }
  const [err, setErr] = useState(null);
  const [now, setNow] = useState(Date.now());
  const [received, setReceived] = useState([]);  // [{ key, url, at }] — across regenerated codes
  const canvasRef = useRef(null);
  const tokRef = useRef(null);
  const seen = useRef(new Set());
  const polling = useRef(false);
  const alive = useRef(true);
  const cbs = useRef({ onFiles, onChanged });
  cbs.current = { onFiles, onChanged };
  const local = target && target.kind !== 'step';

  const poll = useCallback(async (cur) => {
    const tk = cur || tokRef.current;
    if (!tk || polling.current) return;
    polling.current = true;
    try {
      const r = await uploadTokensAPI.status(tk.id);
      const files = Array.isArray(r.data && r.data.files) ? r.data.files : [];
      const fresh = files.filter((f) => !seen.current.has(`${tk.id}:${f.id}`));
      if (fresh.length) {
        fresh.forEach((f) => seen.current.add(`${tk.id}:${f.id}`));
        setReceived((x) => [...x, ...fresh.map((f) => ({ key: `${tk.id}:${f.id}`, url: f.url, at: Date.now() }))]);
        if (local) { if (cbs.current.onFiles) cbs.current.onFiles(fresh.map((f) => f.url)); }
        else if (cbs.current.onChanged) cbs.current.onChanged();
      }
    } catch (e) { /* transient — next tick retries */ }
    finally { polling.current = false; }
  }, [local]);

  const revoke = (tk) => { if (tk) uploadTokensAPI.revoke(tk.id).catch(() => {}); };

  const create = useCallback(async () => {
    setErr(null); setTok(null);
    try {
      const r = await uploadTokensAPI.create(profileId, target);
      const tk = { id: r.data.id, token: r.data.token, expires_at: r.data.expires_at };
      if (!alive.current) { revoke(tk); return; }
      tokRef.current = tk; setTok(tk);
    } catch (e) {
      const st = e && e.response && e.response.status;
      const msg = st === 404 && !(e.response.data && e.response.data.error)
        ? t('后端未部署', 'Not available yet')
        : (e && e.response && e.response.data && e.response.data.error) || t('二维码生成失败', 'Could not create the code');
      setErr(msg);
    }
  }, [profileId, JSON.stringify(target)]);

  useEffect(() => { create(); }, [create]);
  // Revoke whatever code is live when the dialog goes away (完成 / ✕ / the 客户记录 closing).
  useEffect(() => {
    alive.current = true;
    return () => { alive.current = false; revoke(tokRef.current); tokRef.current = null; };
  }, []);

  useEffect(() => {
    const a = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(a);
  }, []);
  useEffect(() => {
    if (!tok) return undefined;
    const b = setInterval(() => poll(), 2000);
    return () => clearInterval(b);
  }, [tok, poll]);

  useEffect(() => {
    if (!tok || !canvasRef.current) return;
    QRCode.toCanvas(canvasRef.current, phoneUploadUrl(tok.token), { width: 192, margin: 1, errorCorrectionLevel: 'M', color: { dark: INK, light: '#FFFDF9' } }, () => {});
  }, [tok]);

  const left = tok ? (Date.parse(tok.expires_at) - now) / 1000 : 0;
  const expired = !!tok && left <= 0;

  const regenerate = async () => {
    const old = tokRef.current;
    if (old) { await poll(old); revoke(old); }
    tokRef.current = null;
    create();
  };
  const finish = async () => {
    const cur = tokRef.current;
    if (cur) await poll(cur);
    revoke(cur); tokRef.current = null;
    onClose();
  };

  const btn = { height: 40, padding: '0 16px', border: '1px solid rgba(17,17,17,.25)', background: 'transparent', fontFamily: 'inherit', fontSize: 14, cursor: 'pointer', borderRadius: 2, color: INK };
  const thumb = { width: 64, height: 64, flex: 'none', borderRadius: 2, border: `1px solid ${LINE}`, objectFit: 'cover', display: 'block' };

  const body = (
    <div onClick={finish} style={{ position: 'fixed', inset: 0, background: 'rgba(20,18,16,.38)', zIndex: 5000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 }}>
      <div role="dialog" aria-modal="true" aria-label={t('用手机扫码上传', 'Upload from your phone')} onClick={(e) => e.stopPropagation()}
        style={{ width: 'min(560px, 100%)', maxHeight: 'calc(100vh - 32px)', overflowY: 'auto', background: '#F6F3EE', color: INK, border: `1px solid ${LINE}`, borderRadius: 2, padding: '18px 22px 20px', boxSizing: 'border-box', display: 'flex', flexDirection: 'column', gap: 16, fontFamily: "'Jost', 'Noto Sans SC', sans-serif" }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <span style={{ fontFamily: SERIF, fontSize: 24, fontWeight: 500 }}>{t('用手机扫码上传', 'Upload from your phone')}</span>
          <button type="button" aria-label={t('关闭', 'Close')} onClick={finish} style={{ width: 44, height: 44, border: 0, background: 'transparent', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', color: INK }}>
            <X size={18} strokeWidth={1.4} />
          </button>
        </div>
        {where && <div style={{ fontSize: 14, color: SUB, overflowWrap: 'anywhere' }}>{t('传到：', 'Goes to: ')}{where}</div>}
        <div style={{ display: 'flex', gap: 22, alignItems: 'center', flexWrap: 'wrap' }}>
          <div style={{ width: 220, height: 220, flex: 'none', background: '#FFFDF9', border: '1px solid rgba(17,17,17,.15)', boxSizing: 'border-box', display: 'flex', alignItems: 'center', justifyContent: 'center', position: 'relative' }}>
            {tok && <canvas ref={canvasRef} role="img" aria-label={t('上传二维码', 'Upload QR code')} style={{ width: 192, height: 192, opacity: expired ? 0.12 : 1 }} />}
            {!tok && !err && <span style={{ fontSize: 13, color: MUTE }}>{t('正在生成…', 'Creating…')}</span>}
            {err && <span style={{ fontSize: 13, color: '#9E2B25', padding: 12, textAlign: 'center' }}>{err}</span>}
            {expired && (
              <span style={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 14, color: INK, textAlign: 'center', padding: 16 }}>
                {t('二维码已失效，请换一个', 'Expired — make a new one')}
              </span>
            )}
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10, fontSize: 14, lineHeight: 1.6, color: SUB, minWidth: 0, flex: '1 1 200px' }}>
            <span>{t('1　手机相机对准二维码', '1  Point your phone camera at the code')}</span>
            <span>{t('2　打开网页，拍照或从相册选', '2  Open the page, take or pick photos')}</span>
            <span>{t('3　点上传，照片会自动出现在这里', '3  Tap upload — they appear here')}</span>
            {tok && (
              <span style={{ color: expired ? '#9E2B25' : GOLD_TEXT, marginTop: 6 }}>
                {expired ? t('已失效', 'Expired') : t(`${mmss(left)} 后失效 · 只能传到这一格`, `Expires in ${mmss(left)} · this slot only`)}
              </span>
            )}
          </div>
        </div>
        <div style={{ borderTop: `1px solid ${LINE}`, paddingTop: 14, display: 'flex', flexDirection: 'column', gap: 10 }}>
          <span style={{ fontSize: 13, color: SUB, letterSpacing: '.06em' }}>{t(`已从手机收到 · ${received.length} 张`, `Received from phone · ${received.length}`)}</span>
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
            {received.map((f) => (
              <a key={f.key} href={resolvePhotoUrl(f.url) || f.url} target="_blank" rel="noreferrer">
                <img src={resolvePhotoUrl(f.url) || f.url} alt="" style={thumb} />
              </a>
            ))}
            {tok && !expired && (
              <div style={{ ...thumb, border: '1px dashed rgba(17,17,17,.35)', background: '#FFFDF9', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 11, color: MUTE }}>
                {t('等待中…', 'Waiting…')}
              </div>
            )}
          </div>
        </div>
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, flexWrap: 'wrap' }}>
          <button type="button" onClick={regenerate} style={btn}>{t('换一个二维码', 'New code')}</button>
          <button type="button" onClick={finish} style={{ ...btn, background: INK, color: '#F6F3EE', borderColor: INK }}>{t('完成', 'Done')}</button>
        </div>
      </div>
    </div>
  );
  return typeof document !== 'undefined' ? createPortal(body, document.body) : body;
}

// Helper for local kinds: append phone photos to an image list capped at `max`; toasts the overflow.
export function appendCapped(setList, urls, max = 9, isZh = true) {
  setList((cur) => {
    const room = Math.max(0, max - cur.length);
    if (urls.length > room) toast.info(isZh ? `最多 ${max} 张，多出的 ${urls.length - room} 张没有加入` : `Max ${max} photos — ${urls.length - room} skipped`);
    return [...cur, ...urls.slice(0, room)];
  });
}

export default QrUploadButton;
